import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, History, Settings, Shield, LogOut, ChevronDown, ChevronRight, Plus, FileBarChart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [historialOpen, setHistorialOpen] = useState(true);
  const [nuevaRetencionOpen, setNuevaRetencionOpen] = useState(true);

  const nuevaRetencionItems = [
    { path: '/retenciones/crear/iva', label: 'Retención IVA', color: 'bg-blue-400' },
    { path: '/retenciones/crear/islr', label: 'Retención ISLR', color: 'bg-amber-400' },
  ];

  const historialItems = [
    { path: '/retenciones/historial', label: 'Todos', filter: null },
    { path: '/retenciones/historial/iva', label: 'Historial IVA', filter: 'IVA' },
    { path: '/retenciones/historial/islr', label: 'Historial ISLR', filter: 'ISLR' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isHistorialActive = location.pathname.includes('/retenciones/historial');
  const isNuevaRetencionActive = location.pathname.includes('/retenciones/crear');

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-slate-900 text-slate-300 flex flex-col">
      <div className="p-2 border-b border-slate-800">
        <div className="bg-white rounded-lg py-2 px-3 flex items-center justify-center">
          <img src="/logo.png" alt="RetenGO" className="w-full h-auto" />
        </div>
      </div>

      {user && (
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-800/50">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Empresa</p>
          <p className="text-sm font-semibold text-white mt-1">{user.nombre_empresa}</p>
          <p className="text-xs text-slate-400 font-mono mt-1">{user.rif_empresa}</p>
        </div>
      )}

      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {/* Dashboard */}
          <li>
            <Link
              to="/dashboard"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                location.pathname === '/dashboard'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <LayoutDashboard size={20} />
              <span className="font-medium text-sm">Dashboard</span>
            </Link>
          </li>

          {/* Nueva Retención con sub-menú */}
          <li>
            <button
              onClick={() => setNuevaRetencionOpen(!nuevaRetencionOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                isNuevaRetencionActive
                  ? 'bg-green-600/20 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Plus size={20} />
                <span className="font-medium text-sm">Nueva Retención</span>
              </div>
              {nuevaRetencionOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            
            {nuevaRetencionOpen && (
              <ul className="mt-1 ml-4 space-y-1">
                {nuevaRetencionItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'bg-green-600 text-white shadow-lg'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${item.color}`}></span>
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>

          {/* Proveedores */}
          <li>
            <Link
              to="/proveedores"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                location.pathname === '/proveedores'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Users size={20} />
              <span className="font-medium text-sm">Proveedores</span>
            </Link>
          </li>
          
          {/* Historial con sub-menú */}
          <li>
            <button
              onClick={() => setHistorialOpen(!historialOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                isHistorialActive
                  ? 'bg-blue-600/20 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <History size={20} />
                <span className="font-medium text-sm">Historial</span>
              </div>
              {historialOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            
            {historialOpen && (
              <ul className="mt-1 ml-4 space-y-1">
                {historialItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${
                          item.filter === 'IVA' ? 'bg-blue-400' : 
                          item.filter === 'ISLR' ? 'bg-amber-400' : 'bg-slate-400'
                        }`}></span>
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>

          {/* Reportes */}
          <li>
            <Link
              to="/reportes"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                location.pathname === '/reportes'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <FileBarChart size={20} />
              <span className="font-medium text-sm">Reportes</span>
            </Link>
          </li>

          {/* Configuración */}
          <li>
            <Link
              to="/configuracion"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                location.pathname === '/configuracion'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Settings size={20} />
              <span className="font-medium text-sm">Configuración</span>
            </Link>
          </li>

          {/* Panel Admin (solo admin) */}
          {isAdmin && (
            <li>
              <Link
                to="/admin"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === '/admin'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Shield size={20} />
                <span className="font-medium text-sm">Panel Admin</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-800">
        {user && (
          <div className="mb-3 text-xs text-slate-400">
            <p>{user.email}</p>
          </div>
        )}
        <Button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center space-x-2"
        >
          <LogOut size={18} />
          <span>Cerrar Sesión</span>
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;